<script>
import BaseView from "@/mixins/BaseView.vue";
import DriversList from "@/views/drivers/components/DriversList.vue";
import TrucksMixin from "@/views/fleets/mixins/TrucksMixin.vue";
import TrucksList from "@/views/fleets/components/TrucksList.vue";
import UsersMixin from "@/views/users/mixins/UsersMixin.vue";
import UserFiles from "@/views/users/components/UserFiles.vue";
import SidebarLayout from "@/components/layout/SidebarLayout.vue";
import SidebarToggle from "@/components/buttons/SidebarToggle.vue";
import UserViewUserInfo from "@/views/users/components/view/UserViewUserInfo.vue";
import UserViewDriverInfo from "@/views/users/components/view/UserViewDriverInfo.vue";
import UserViewDispatcherNote from "@/views/users/components/view/UserViewDispatcherNote.vue";
import UserViewDriverNote from "@/views/users/components/view/UserViewDriverNote.vue";
import UserViewOwnerInfo from "@/views/users/components/view/UserViewOwnerInfo.vue";
import UserViewSidebar from "@/views/users/components/view/UserViewSidebar.vue";
import NotFoundMixin from "@/mixins/not-found.mixin.ts";
import NotFound from "@/views/notFound/NotFound.vue";
import UserAdditionalActions from "@/views/users/components/UserAdditionalActions.vue";
import UserViewCoordinatorInfo from "@/views/users/components/view/UserViewCoordinatorInfo.vue";
import OmniTabs from "@/components/omni/OmniTabs.vue";
import { mapState } from "pinia";
import { useAppStore } from "@/store/app.store.ts";
import OmniTooltip from "@/components/omni/OmniTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto.ts";

export default {
  name: "UserView",
  components: {
    OmniTooltip,
    OmniTabs,
    UserAdditionalActions,
    NotFound,
    UserViewSidebar,
    UserViewCoordinatorInfo,
    UserViewOwnerInfo,
    UserViewDriverNote,
    UserViewDispatcherNote,
    UserViewDriverInfo,
    UserViewUserInfo,
    SidebarToggle,
    SidebarLayout,
    DriversList,
    TrucksList,
    UserFiles,
  },
  mixins: [BaseView, UsersMixin, TrucksMixin, NotFoundMixin],
  data() {
    return {
      user: { companies: [] },
      trucksParams: { owner: this.$route.params.id },
      driversParams: { owner: this.$route.params.id },

      loading: false,
      tab: "overview",
    };
  },
  computed: {
    ...mapState(useAppStore, ["dispatchers", "dispatcher"]),
    editLink() {
      return {
        name: this.$route.name.replace("view", "edit"),
        params: { id: this.user.id },
      };
    },
    hasAccessToEdit() {
      return this.hasAccess(DispatcherPermissionValue.EditOrCreateUser);
    },
  },
  async mounted() {
    await this.loadInfo();
  },
  methods: {
    async loadDrivers() {
      if (!this.user.isOwner) return;

      try {
        const resp = await this.$api.users.findDrivers({
          owner: this.$route.params.id,
        });
        this.drivers = resp.data.filter((d) => d.id !== this.user.driverId);
      } catch (error) {
        console.error("Error load owner drivers: ", error);
      }
    },
    async loadUser() {
      let user = { coordinators: [], owners: [], companies: [] };

      const resp = await this.$api.users.findUserById(this.$route.params.id);
      if (!this.validateEntityResponse(resp)) return;

      user = { ...user, ...resp.data };

      if (user.isDriver) {
        const driverResponse = await this.$api.users.findDriverById(user.id);

        if (driverResponse.success) {
          user = { ...user, ...driverResponse.data };
        }

        const coordinatorsResponse = await this.$api.users.findOwnerCoordinators(user.ownerId);
        if (coordinatorsResponse.success) {
          user.coordinators = coordinatorsResponse.data;
        }
      }

      this.setTitle("User " + user.name, "Users");

      const ownerId = user.isOwner ? user.id : user.isDriver ? user.ownerId : null;

      if (ownerId) {
        const companiesResponse = await this.$api.users.findCompanies(ownerId);

        if (companiesResponse.success) {
          user.companies = companiesResponse.data;

          if (user.isDriver && user.ownerCompanyId) {
            user.driverOwnerCompany = user.companies.find((c) => c.id === user.ownerCompanyId) || null;
          }
        }
      }

      if (user.isOwner) {
        const coordinatorsResponse = await this.$api.users.findOwnerCoordinators(user.id);
        if (coordinatorsResponse.success) {
          user.coordinators = coordinatorsResponse.data;
        }
      }

      if (user.isCoordinator) {
        const ownersResponse = await this.$api.users.findCoordinatorOwners(user.id);
        if (ownersResponse.success) {
          user.owners = ownersResponse.data;
        }
      }

      this.user = user;
    },
    async loadTrucks() {
      if (!this.user.isOwner) return;

      try {
        const resp = await this.$api.trucks.findTrucks({
          owner: this.$route.params.id,
        });
        this.trucks = resp.data;
      } catch (error) {
        console.error("Error load owner trucks: ", error);
      }
    },
    async loadInfo() {
      this.loading = true;

      await Promise.all([this.loadUser(), this.loadDrivers(), this.loadTrucks()]);

      this.loading = false;
    },
    isSidebarOpenDefault() {
      let windowWidth = window.innerWidth;
      return windowWidth > 1250;
    },
  },
};
</script>

<template>
  <v-container v-if="!entityNotFound" :fluid="true" class="pt-6 pb-10 pl-6 pr-6 height-100 users-page">
    <sidebar-layout :sidebar-opened="isSidebarOpenDefault()">
      <div class="w-100">
        <div class="users-page__header d-flex justify-space-between align-center flex-wrap mb-4">
          <div class="users-page__name text-grey-darken-4 title font-weight-medium font-size-20">
            {{ user.name }}
            <v-chip
              v-if="user.archived !== null && !loading"
              :label="true"
              class="ml-4"
              color="grey-darken-2"
              variant="flat"
            >
              Archived
            </v-chip>
          </div>
          <div v-if="user.id" class="d-flex align-center">
            <omni-tooltip :disabled="hasAccessToEdit">
              <template #activator>
                <v-btn
                  :class="{ 'btn--disabled': user.archived !== null }"
                  :disabled="user.archived !== null || !hasAccessToEdit"
                  :to="editLink"
                  class="text-uppercase font-weight-600 text-white mr-2"
                  color="primary"
                  variant="flat"
                >
                  <v-icon class="mr-2" size="20"> mdi-pencil-outline</v-icon>
                  {{ $t("general.edit") }} profile
                </v-btn>
              </template>

              {{ $t("permission.edit-user") }}
            </omni-tooltip>
            <sidebar-toggle class="mr-2"></sidebar-toggle>
            <user-additional-actions :user="user" @change="loadUser"></user-additional-actions>
          </div>
        </div>

        <v-card variant="flat">
          <omni-tabs v-model="tab" class="rounded">
            <v-tab class="text-uppercase" value="overview">Overview</v-tab>
            <v-tab v-if="user.isOwner" value="drivers"> Drivers</v-tab>
            <v-tab v-if="user.isOwner" value="trucks"> Trucks</v-tab>
            <v-tab class="text-uppercase" value="files">Files</v-tab>
          </omni-tabs>

          <v-window v-model="tab" :touch="false">
            <v-window-item value="overview">
              <user-view-user-info :user="user"></user-view-user-info>
              <user-view-driver-info :user="user"></user-view-driver-info>
              <user-view-dispatcher-note :user="user"></user-view-dispatcher-note>
              <user-view-driver-note :user="user"></user-view-driver-note>
              <user-view-owner-info :user="user"></user-view-owner-info>
              <user-view-coordinator-info :user="user"></user-view-coordinator-info>
            </v-window-item>

            <v-window-item v-if="user.isOwner" value="drivers">
              <div class="mt-4">
                <drivers-list :compact="true" :search-params="driversParams" />
              </div>
            </v-window-item>

            <v-window-item v-if="user.isOwner" value="trucks">
              <div class="mt-4">
                <trucks-list
                  :available-columns="['name', 'status', 'driver', 'size', 'available', 'current']"
                  :compact="true"
                  :search-params="trucksParams"
                />
              </div>
            </v-window-item>

            <v-window-item class="pa-3" value="files">
              <user-files />
            </v-window-item>
          </v-window>
        </v-card>
      </div>
      <template #sidebar>
        <user-view-sidebar :user="user"></user-view-sidebar>
      </template>
    </sidebar-layout>
  </v-container>
  <not-found v-else icon="mdi-account-remove-outline" text="User not found"></not-found>
</template>

<style lang="scss" scoped>
b {
  color: black;
}

h3 {
  margin: 0 0 3px;
  color: black;
}

h4 {
  margin: 0 0 2px;
  color: black;
}

.v-card {
  background-color: transparent !important;
}
</style>
